import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import { Check, Plus } from 'lucide-react';
import { SAVE_DOCUMENT_TEXT_MUTATION } from '../graphql/mutations';
import { DOCUMENT_CONTENT_QUERY } from '../graphql/queries';
import { applyLocalSave, enqueueSave, isOfflineError, removeOutboxEntry } from '../lib/outbox';
import { Markdown } from './Markdown';
import { useWikilinkResolver } from '../hooks/useWikilinkResolver';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import {
  appendItemLine,
  toggleItemLine,
  type JournalView,
  type ListView,
  type NoteView,
} from '../lib/docView';

/**
 * The per-type rendered views over one wiki document (ROADMAP "Rendered
 * views"). Display parsing lives in `lib/docView.ts`; these components render
 * the parsed shapes with the pre-pivot look. The ONLY writes here are text
 * edits: the list view rebuilds the full blob (flip one `- [ ]` line, or
 * append one) and sends it through the existing
 * `saveDocumentText(path, content, expectedVersion)` compare-and-swap — no
 * structured write-back, no new backend write paths.
 */

interface ListDocViewProps {
  path: string;
  /** The raw stored blob the view was parsed from (the CAS rebuild base). */
  content: string;
  /** `documentContent.version`, passed back as `expectedVersion`. */
  version: number;
  view: ListView;
  /** Reloads the page's `documentContent` after a CONFLICT (stale view). */
  onStaleReload: () => Promise<unknown>;
}

type ListSaveError = 'conflict' | 'generic';

/**
 * Interactive checklist view: round tap-to-toggle checkboxes, strikethrough
 * done items, non-item lines rendered as Markdown in place, and an add-item
 * input. "The text editor with buttons" — every interaction is a whole-blob
 * text save guarded by the version compare-and-swap; a concurrent edit
 * surfaces as a calm reload, never a clobber. Edits are optimistic: the view
 * updates instantly and saves run serialized in the background.
 */
export function ListDocView({ path, content, version, view, onStaleReload }: ListDocViewProps) {
  const client = useApolloClient();
  const resolveWikilink = useWikilinkResolver();
  const [newItemText, setNewItemText] = useState('');
  const [saveError, setSaveError] = useState<ListSaveError | null>(null);
  // The line whose checkbox was just tapped — it gets the check-pop
  // micro-animation (reduced-motion drops it via the animation class).
  const [poppedLine, setPoppedLine] = useState<number | null>(null);

  // Optimistic saves: a tap writes the new blob into the Apollo cache at once
  // (the view re-renders from it) and the save runs in the background. Saves
  // are serialized — the CAS needs each one based on the version the previous
  // one returned — and coalesced: taps landing mid-save collapse into the
  // newest blob, sent next. `pendingRef` is that newest unsent/unconfirmed
  // blob; `confirmedRef` is the last server truth, restored if a save fails.
  const pendingRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  const confirmedRef = useRef({ content, version });
  useEffect(() => {
    if (!savingRef.current && pendingRef.current === null) {
      confirmedRef.current = { content, version };
    }
  }, [content, version]);

  const [saveText] = useMutation<{
    saveDocumentText: { path: string; content: string; version: number; updatedAt: string | null };
  }>(SAVE_DOCUMENT_TEXT_MUTATION);

  const writeCache = (nextContent: string, nextVersion: number, updatedAt?: string | null) => {
    client.writeQuery({
      query: DOCUMENT_CONTENT_QUERY,
      variables: { path },
      data: {
        documentContent: {
          __typename: 'DocumentContent',
          path,
          content: nextContent,
          updatedAt: updatedAt ?? new Date().toISOString(),
          version: nextVersion,
        },
      },
    });
  };

  const flush = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    let landed = false;
    try {
      while (pendingRef.current !== null) {
        const sending = pendingRef.current;
        const expectedVersion = confirmedRef.current.version;
        try {
          const { data } = await saveText({
            variables: { path, content: sending, expectedVersion },
          });
          // A landed save supersedes any offline edit still queued for this path.
          removeOutboxEntry(path);
          landed = true;
          const saved = data?.saveDocumentText;
          confirmedRef.current = {
            content: saved?.content ?? sending,
            version: saved?.version ?? expectedVersion,
          };
          if (pendingRef.current === sending) {
            pendingRef.current = null;
            if (saved) writeCache(saved.content, saved.version, saved.updatedAt);
          }
          // Otherwise newer taps are queued; the cache already shows them and
          // the loop sends them on the version this save returned.
        } catch (err) {
          const unsaved = pendingRef.current ?? sending;
          pendingRef.current = null;
          if (isOfflineError(err)) {
            // Offline: keep the edit on this device and flush when the network
            // is back (see lib/outbox.ts). The cache already shows it.
            enqueueSave(path, unsaved, expectedVersion);
            applyLocalSave(client, path, unsaved);
            return;
          }
          const code = (err as ApolloError).graphQLErrors?.[0]?.extensions?.code;
          if (code === 'CONFLICT') {
            setSaveError('conflict');
            // Reload the latest so the next tap works against the current text.
            await onStaleReload().catch(() => {});
          } else {
            setSaveError('generic');
            // Undo the optimistic edit: show what the server actually holds.
            writeCache(confirmedRef.current.content, confirmedRef.current.version);
          }
          return;
        }
      }
    } finally {
      savingRef.current = false;
      // Refresh the tree in the background so updatedAt ordering catches up.
      if (landed) client.refetchQueries({ include: ['Documents'] }).catch(() => {});
    }
  };

  const applyEdit = (nextContent: string) => {
    setSaveError(null);
    pendingRef.current = nextContent;
    writeCache(nextContent, confirmedRef.current.version);
    void flush();
  };

  // Edits build on the newest local blob, so rapid taps stack instead of
  // racing the re-render.
  const currentContent = () => pendingRef.current ?? content;

  const handleToggle = async (lineIndex: number) => {
    setPoppedLine(lineIndex);
    const next = toggleItemLine(currentContent(), lineIndex);
    if (next === null) {
      // The view is stale (that line is no longer an item) — reload, don't write.
      setSaveError('conflict');
      await onStaleReload().catch(() => {});
      return;
    }
    applyEdit(next);
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const text = newItemText.trim();
    if (!text) return;
    setNewItemText('');
    applyEdit(appendItemLine(currentContent(), text));
  };

  return (
    <section
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      data-testid="list-doc-view"
    >
      {saveError && (
        <div
          role="alert"
          className="bg-[color-mix(in_oklab,var(--coral-400)_10%,transparent)] border border-[color-mix(in_oklab,var(--coral-400)_35%,transparent)] text-[var(--text-primary)] px-4 py-3 rounded-xl text-sm mb-4"
        >
          {saveError === 'conflict'
            ? "This document changed somewhere else, so that wasn't saved. It's showing the latest now — try again."
            : "That wasn't saved. Something went wrong on our end — try again."}
        </div>
      )}

      {view.items.length === 0 && view.blocks.every((b) => b.kind !== 'markdown') ? (
        <p className="text-[var(--text-secondary)] text-center py-6">
          Nothing here yet — add the first thing below.
        </p>
      ) : (
        <div className="space-y-3">
          {view.blocks.map((block, blockIndex) =>
            block.kind === 'markdown' ? (
              <Markdown
                key={`md-${blockIndex}`}
                content={block.text}
                className="font-reading text-sm sm:text-base"
                resolveWikilink={resolveWikilink}
              />
            ) : (
              <ul key={`items-${blockIndex}`} className="space-y-1">
                {block.items.map((item) => (
                  <li
                    key={item.lineIndex}
                    className="group flex items-center gap-3 p-2 rounded-xl hover:bg-[color-mix(in_oklab,var(--text-primary)_4%,transparent)]"
                    data-testid="list-doc-item"
                  >
                    <button
                      type="button"
                      onClick={() => handleToggle(item.lineIndex)}
                      className={`relative before:absolute before:content-[''] before:-inset-2.5 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${FOCUS_RING_CLASSES} ${
                        item.checked
                          ? 'bg-[var(--marine-400)] border-[var(--marine-400)] text-white'
                          : 'border-[var(--border)] hover:border-[var(--marine-400)]'
                      } ${item.checked && item.lineIndex === poppedLine ? 'animate-check-pop' : ''}`}
                      role="checkbox"
                      aria-checked={item.checked}
                      aria-label={item.text}
                    >
                      {item.checked && <Check className="w-4 h-4" aria-hidden />}
                    </button>
                    <span
                      className={`flex-1 min-w-0 break-words ${
                        item.checked
                          ? 'line-through text-[var(--text-tertiary)]'
                          : 'text-[var(--text-primary)]'
                      }`}
                    >
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      )}

      <form onSubmit={handleAdd} className="mt-4 flex gap-2">
        <label htmlFor="list-doc-new-item" className="sr-only">
          Add an item
        </label>
        <input
          id="list-doc-new-item"
          type="text"
          enterKeyHint="done"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          className="input flex-1"
          placeholder="Add an item…"
          maxLength={500}
        />
        <button
          type="submit"
          className="btn btn-primary gap-1 shrink-0"
          disabled={!newItemText.trim()}
        >
          <Plus className="w-4 h-4" aria-hidden /> Add
        </button>
      </form>
    </section>
  );
}

interface JournalDocViewProps {
  view: JournalView;
}

/**
 * Emoji stand-ins for legacy pivot-1 categories, used when an old moment has a
 * category but (like all legacy moments) no leading emoji of its own.
 */
const CATEGORY_EMOJI: Record<string, string> = {
  body: '🏃',
  mind: '🧠',
  work: '💼',
  care: '🫶',
  joy: '😊',
};

/** Tile background: the legacy category color when present, else the sun gradient. */
function momentTileStyle(category: string | null): React.CSSProperties {
  if (category) return { backgroundColor: `var(--category-${category})` };
  return { background: 'linear-gradient(135deg, var(--sun-300) 0%, var(--sun-500) 100%)' };
}

/**
 * Journal timeline view (display-only): each moment as a glass card on a
 * sun-colored timeline — emoji tile (category-colored for legacy moments),
 * moment text, monospace time. The page h1 already names the day, so the view
 * carries no title of its own.
 */
export function JournalDocView({ view }: JournalDocViewProps) {
  return (
    <div data-testid="journal-doc-view">
      <div className="space-y-2.5 pl-4 border-l-2 border-[var(--sun-500)] ml-2">
        {view.moments.map((moment, index) => (
          <div
            key={index}
            className="glass-panel rounded-2xl p-4 border border-[var(--card-border)] shadow-soft"
            data-testid="journal-moment"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="p-2 rounded-xl text-white shadow-sm shrink-0"
                  style={momentTileStyle(moment.category)}
                  aria-hidden
                >
                  <span className="text-lg">
                    {moment.emoji ?? (moment.category && CATEGORY_EMOJI[moment.category]) ?? '✨'}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base text-[var(--text-primary)] font-medium leading-snug break-words">
                    {moment.text}
                  </p>
                  {moment.secondary && (
                    <p className="text-sm text-[var(--text-secondary)] mt-0.5 break-words">
                      {moment.secondary}
                    </p>
                  )}
                </div>
              </div>
              {moment.time && (
                <span
                  className="text-xs text-[var(--text-tertiary)] font-mono shrink-0"
                  data-testid="journal-moment-time"
                >
                  {moment.time}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface NoteDocViewProps {
  view: NoteView;
  /** `documentContent.updatedAt` for the quiet footer; null hides it. */
  updatedAt: string | null;
}

/** "Updated today/yesterday/Jul 7, 2026" in the local tz. */
function formatUpdated(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'Updated today';
  if (d.toDateString() === y.toDateString()) return 'Updated yesterday';
  return `Updated ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

/**
 * Reading view for a long note (display-only): the full prose body as a
 * reading card with a quiet "Updated …" footer. The user navigated here to
 * read this document, so nothing is collapsed behind a "Read more" tap
 * (design review D7). The page h1 already shows the note's title, so the card
 * repeats no heading of its own.
 */
export function NoteDocView({ view, updatedAt }: NoteDocViewProps) {
  const resolveWikilink = useWikilinkResolver();
  const meta = formatUpdated(updatedAt);

  return (
    <article
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      data-testid="note-doc-view"
      aria-label={view.title}
    >
      <Markdown
        content={view.body}
        className="font-reading text-sm sm:text-base"
        resolveWikilink={resolveWikilink}
      />
      {meta && <p className="mt-4 text-xs text-[var(--text-tertiary)]">{meta}</p>}
    </article>
  );
}
